/**
 * Renders a CoreUI brand icon for a Composio toolkit slug.
 *
 * Icons are served statically from `/brand-icons/cib-<slug>.svg` (copied
 * from `@coreui/icons` at build time). Composio slugs don't always match
 * CoreUI's naming (`googlecalendar` vs `google-calendar`, `google_drive`
 * vs `google-drive`, etc.), so we try a handful of normalized forms
 * before falling back to either the Composio-provided logo URL or a
 * monogram tile.
 */

import { useMemo, useState } from 'react';

/** Aliases for toolkits whose CoreUI name doesn't match the Composio slug. */
const SLUG_ALIASES: Record<string, string> = {
  googlecalendar: 'google',
  googledrive: 'google',
  googledocs: 'google',
  googlesheets: 'google',
  googleforms: 'google',
  googlemeet: 'google',
  googleads: 'google-ads',
  googleanalytics: 'google-analytics',
  gmail: 'gmail',
  outlook: 'microsoft-outlook',
  microsoftteams: 'microsoft',
  onedrive: 'microsoft',
  sharepoint: 'microsoft',
  ms_teams: 'microsoft',
  teams: 'microsoft',
  github: 'github',
  gitlab: 'gitlab',
  bitbucket: 'bitbucket',
  composio: 'c',
  openai: 'openai', // may 404 — fallback handles it
};

function candidateFilenames(slug: string): string[] {
  const s = slug.toLowerCase();
  const tried = new Set<string>();
  const push = (v: string) => tried.add(v.replace(/[^a-z0-9-]/g, ''));
  if (SLUG_ALIASES[s]) push(SLUG_ALIASES[s]);
  push(s);
  push(s.replace(/_/g, '-'));
  // "googlecalendar" -> try splitting on known prefixes
  for (const prefix of ['google', 'microsoft', 'adobe', 'amazon']) {
    if (s.startsWith(prefix) && s.length > prefix.length) {
      push(`${prefix}-${s.slice(prefix.length)}`);
    }
  }
  return [...tried].filter(Boolean).map(name => `/brand-icons/cib-${name}.svg`);
}

export function BrandIcon({
  slug,
  fallbackLogo,
  name,
  size = 28,
  className = '',
}: {
  slug: string;
  fallbackLogo?: string | null;
  name?: string;
  size?: number;
  className?: string;
}) {
  const candidates = useMemo(() => {
    const c = candidateFilenames(slug);
    if (fallbackLogo) c.push(fallbackLogo);
    return c;
  }, [slug, fallbackLogo]);
  const [idx, setIdx] = useState(0);
  const style = { width: size, height: size };

  if (idx >= candidates.length) {
    // Monogram tile — first two letters of the display name or slug.
    const mono = (name ?? slug).replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
    return (
      <div
        aria-hidden
        className={`flex items-center justify-center rounded-md bg-muted text-[10px] font-semibold text-muted-foreground ${className}`}
        style={style}
      >
        {mono || '??'}
      </div>
    );
  }

  return (
    <img
      src={candidates[idx]}
      alt=""
      aria-hidden
      width={size}
      height={size}
      className={`object-contain ${className}`}
      style={style}
      onError={() => setIdx(i => i + 1)}
      loading="lazy"
      decoding="async"
    />
  );
}
